import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, CATEGORIES } from "@/lib/api";
import { FadeUp, Overline } from "@/components/site/Reveal";
import ProductCard from "@/components/site/ProductCard";

export default function CategoryPage() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [products, setProducts] = useState(null);

  useEffect(() => {
    setCat(null);
    setProducts(null);
    api
      .get("/categories")
      .then((d) => setCat(d.data.find((c) => c.slug === slug) || null))
      .catch(() => setCat(CATEGORIES.find((c) => c.slug === slug) || null));
    api
      .get("/products", { params: { category: slug } })
      .then((d) => setProducts(d.data))
      .catch(() => setProducts([]));
  }, [slug]);

  return (
    <div className="pt-28 sm:pt-32" data-testid="category-page">
      <header className="mx-auto max-w-7xl px-6">
        <div className="grid items-end gap-10 pb-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Overline className="text-terra">{cat ? cat.overline : "Systems"}</Overline>
            <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-forest-700 sm:text-5xl" data-testid="category-title">
              {cat ? cat.name : "Products"}
            </h1>
            {cat && (
              <p className="mt-4 max-w-xl text-base leading-relaxed text-moss">{cat.description}</p>
            )}
          </div>
          {cat && (
            <div className="lg:col-span-5">
              <div className="clip-notch overflow-hidden">
                <img src={cat.image} alt={cat.name} className="h-48 w-full object-cover object-top sm:h-60" />
              </div>
            </div>
          )}
        </div>
      </header>

      <section className="border-t border-forest-700/10 bg-bone-dark py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          {products === null ? (
            <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3" data-testid="category-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse rounded-lg">
                  <div className="aspect-[4/5] w-full rounded-lg bg-sage" />
                  <div className="mt-4 h-4 w-1/3 rounded bg-sage" />
                  <div className="mt-2 h-4 w-2/3 rounded bg-sage" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center" data-testid="category-empty">
              <p className="font-display text-2xl font-bold text-forest-700">
                Stock is being updated.
              </p>
              <p className="mt-2 text-moss">
                Ask us what's available right now —{" "}
                <Link to="/contact" className="font-bold text-terra underline underline-offset-4">
                  request a quote
                </Link>
                .
              </p>
            </div>
          ) : (
            <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 xl:grid-cols-3" data-testid="category-product-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16" data-testid="category-cross-links">
        <FadeUp>
          <div className="flex flex-col items-start justify-between gap-6 rounded-xl border border-forest-700/10 bg-bone p-8 sm:flex-row sm:items-center">
            <div>
              <h2 className="font-display text-xl font-bold text-forest-700">
                Not sure which system fits your home?
              </h2>
              <p className="mt-1 text-sm text-moss">Book a free site inspection — we'll measure and recommend.</p>
            </div>
            <Link
              to="/book-inspection"
              data-testid="category-book-inspection"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-terra-dark"
            >
              Book a free inspection
            </Link>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
